/**
 * Чистая половина всего, что про СПИСАНИЕ на экранах: отбор по причине и
 * само окно списания.
 *
 * Отбор стоит на двух экранах — в разделе «Списание» и на карточке баркода, —
 * и вести себя обязан одинаково: причина, выбранная в одном, называется теми
 * же словами в другом. Окно списания (карточки места и серии) — та же тема, и
 * его правила тем более обязаны жить без React: списание необратимо, и «когда
 * ключ перевыпускается» нельзя проверять глазами на живом складе.
 */

import { stamp } from "@/lib/format";
import type {
  SpisanieByBarcode,
  SpisanieCategory,
  SpisanieEdit,
  SpisanieResult,
} from "@/lib/providers";

/**
 * «Все причины» — НОЛЬ, а не пустая строка.
 *
 * Пустой отбор уже занят: отбор по умолчанию непустой («Не нашли»), и «в
 * адресе ничего нет» означает именно его. Чтобы попросить весь журнал, это
 * нужно сказать явно, а ноль годится потому, что категорией быть не может —
 * `sSerial_spisanie_category` начинается с единицы. То же число понимает и
 * сервис (`repo/spisanie.py::ALL_CATEGORIES`).
 */
export const ALL_CAUSES = "0";

/** Строка отбора: значение как в адресе, имя, счётчик и подсказка. */
export type CauseOption = {
  value: string;
  label: string;
  meta: string;
  title: string;
};

/**
 * Причины для выпадающего отбора, «все» первой строкой.
 *
 * Порядок причин задаёт СЕРВИС — по убыванию числа строк, — и здесь он только
 * сохраняется: пересортируй мы список повторно, отбор переставлял бы пункты от
 * экрана к экрану.
 *
 * Число в правой колонке меряет ТОТ ЖЕ набор, что покажет список: в разделе —
 * весь склад, на карточке баркода — только его вещи. Иначе «Маркетплейс
 * 296 219» над списком из семидесяти восьми списаний читалось бы как обещание
 * открыть триста тысяч строк.
 */
export function causeOptions(
  categories: readonly SpisanieCategory[],
  allLabel = "все причины",
): CauseOption[] {
  const total = categories.reduce((n, one) => n + one.rows, 0);
  return [
    { value: ALL_CAUSES, label: allLabel, meta: String(total), title: allLabel },
    ...categories.map((one) => ({
      value: String(one.category_id),
      label: one.name,
      meta: String(one.rows),
      // Дата последней строки отличает работу склада от истории: «Промо товар
      // · 1 587 · 15.04.2022» — не та причина, по которой списывают сегодня.
      title: one.last
        ? `${one.name}: ${one.rows} · последнее ${stamp(one.last)}`
        : `${one.name}: ни одного списания`,
    })),
  ];
}

/**
 * Как зовётся выбранная причина. Незнакомое значение возвращается как есть:
 * причину могли погасить в справочнике, а ссылка на неё уже уехала сменщику, и
 * честный номер лучше пустой строки.
 */
export function causeName(
  categories: readonly SpisanieCategory[],
  value: string,
): string {
  if (!value || value === ALL_CAUSES) return "все причины";
  return (
    categories.find((one) => String(one.category_id) === value)?.name ?? value
  );
}

// ---------------------------------------------------------------------------
// ОКНО СПИСАНИЯ — кнопка «Списать» на карточках места (задача владельца
// 11.09.26) и серии (задача владельца 16.09.26). Здесь только правила;
// разметка — `components/SpisanieSheet.tsx`.
//
// ОКНО ОДНО НА ОБА ЭКРАНА, а дверей за ним ДВЕ, и различает их то, ЧЕМ НАЗВАНА
// ВЕЩЬ: карточка места знает баркод и место, карточка серии — саму вещь. Из
// этого следует всё остальное различие — потолок штук, ключ однократности и
// форма ответа, — поэтому итог обеих дверей приводится ЗДЕСЬ к одной форме
// `SpisanieOutcome`: разметка итога обязана быть одна, иначе «что получилось»
// читалось бы на двух экранах одного склада по-разному.
// ---------------------------------------------------------------------------

/**
 * КЛЮЧ ОДНОКРАТНОСТИ запроса — то, чем дверь отличает повтор от второй работы.
 *
 * Собран из места и баркода не для красоты: в таблице `wms.ext_request` ключ —
 * единственный след того, кто и о чём спрашивал, и «place-37954-2043791498562»
 * читается там без нашей помощи. Случайный хвост делает его неповторимым.
 *
 * Дефис и точка разрешены дверью (`^[A-Za-z0-9._:\-]+$`), длина укладывается в
 * её 8–64: место и баркод дают около тридцати знаков.
 */
export function requestKey(placeId: number, barcode: string): string {
  return `place-${placeId}-${barcode}-${tail()}`;
}

function tail(): string {
  // `crypto.randomUUID` есть и в браузере (кабинет работает по https, то есть
  // в защищённом контексте), и в Node под тестами. Запасной путь оставлен на
  // случай старого телефона склада: ключ обязан получиться ВСЕГДА — без него
  // окно не сможет отправить запрос вовсе.
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid.slice(0, 8);
  return Math.random().toString(36).slice(2, 10);
}

/**
 * Нужен ли НОВЫЙ ключ перед следующей попыткой. Правило несущее, и оно ровно
 * обратно интуиции.
 *
 * Ответ ПРИШЁЛ — неважно, успех или отказ, — значит запрос дошёл и дверь его
 * уже записала. Повтор с тем же ключом отдал бы ту же запись и не сделал бы
 * ничего; чтобы попробовать снова, нужен новый ключ, и это осознанное
 * действие, а не молчаливый ретрай.
 *
 * Ответа НЕ БЫЛО вовсе (обрыв, таймаут шлюза) — ключ обязан остаться прежним:
 * запрос МОГ дойти и списать вещь. Новый ключ в этом месте списал бы ВТОРУЮ.
 */
export function keepKey(answered: boolean): boolean {
  return !answered;
}

/** Сколько штук можно попросить: не больше, чем лежит, и не больше потолка двери. */
export const MAX_QTY = 20;

export function clampQty(want: number, units: number): number {
  const top = Math.min(units, MAX_QTY);
  if (!Number.isFinite(want)) return 1;
  return Math.max(1, Math.min(Math.trunc(want), Math.max(1, top)));
}

/**
 * Чем кончилось списание — ОДНОЙ строкой для человека.
 *
 * Номера серий названы вслух намеренно: списание необратимо, и единственное,
 * чем человек может проверить нас потом, — номер вещи, ушедшей с учёта. «Готово»
 * без номера этой возможности не даёт.
 */
export function doneMessage(reply: SpisanieByBarcode): string {
  const serials = (reply.results ?? [])
    .filter((one) => one.ok && one.serial)
    .map((one) => one.serial);
  const parts = [`Списано ${reply.done} шт`];
  if (serials.length > 0)
    parts.push(`${serials.length > 1 ? "серии" : "серия"} ${serials.join(", ")}`);
  parts.push(`осталось ${reply.left ?? 0}`);
  return parts.join(" · ");
}

/**
 * Чем кончилось списание — В ОДНОЙ ФОРМЕ, какой бы дверью ни списывали.
 *
 * Дверей две, ответы у них разные (у одной «сколько осталось в месте», у
 * другой разбор по серии), а окно одно — и человек, стоящий над коробом,
 * обязан читать итог одними и теми же словами на обоих экранах.
 */
export type SpisanieOutcome = {
  /** Получилось ли ВСЁ, о чём просили: этим окрашена рамка итога. */
  ok: boolean;
  /**
   * Надо ли перечитать экран — склад мог стать другим.
   *
   * Отдельно от `ok`, и это не педантизм: обе двери отвечают 200 и на
   * частичную работу. По баркоду просили три вещи, списалась одна — `ok`
   * ложно, а состав места УЖЕ другой, и оставленный на экране старый спорил бы
   * со словами «списано 1». Обратный случай — дверь по номеру серии: отказ она
   * тоже отдаёт двухсотым ответом, и перечитывать после него нечего.
   */
  reread: boolean;
  /** Что именно произошло — одной строкой. */
  message: string;
  /** Вторая строка мелким: работы второй раз не было. */
  note?: string;
  /** Отказавшие серии поимённо. Пусто там, где серия одна и отказ уже в `message`. */
  bad: SpisanieResult[];
};

/** Итог двери по БАРКОДУ И МЕСТУ (`POST /api/spisanie/barcode`). */
export function barcodeOutcome(reply: SpisanieByBarcode): SpisanieOutcome {
  const out: SpisanieOutcome = {
    ok: reply.ok,
    reread: reply.done > 0,
    message: doneMessage(reply),
    bad: (reply.results ?? []).filter((one) => !one.ok),
  };
  // Повтор по ключу отдаёт ТОТ ЖЕ ответ, но второго списания не было, и
  // отчитаться о работе дважды экран не вправе.
  if (reply.repeat) out.note = "Повтор того же запроса — второй раз не списывали.";
  return out;
}

/**
 * Итог двери по НОМЕРУ СЕРИИ (`POST /api/spisanie`) — карточка вещи.
 *
 * Дверь пакетная и отвечает 200 даже на отказ, поэтому «получилось ли» читается
 * из строки СВОЕЙ серии, а не из кода ответа. Ищется она номером, а не первой
 * попавшейся: пакет у карточки из одной вещи, но брать «строку номер ноль» там,
 * где ответ разбирает серии поимённо, значит однажды отчитаться о чужой.
 *
 * `already` — состояние уже было таким. Это не работа и не отказ: второй записи
 * в CRM не появилось, и сказать об этом обязательно — иначе экран отчитается о
 * списании, которого он не делал.
 */
export function serialOutcome(
  reply: SpisanieEdit,
  serial: number,
): SpisanieOutcome {
  const row = (reply.results ?? []).find((one) => one.serial === serial);
  if (!row) {
    // Ответ без своей серии — это наш дефект или чужая дверь. Молчать о нём
    // нельзя: человек решит, что вещь списана.
    return {
      ok: false,
      reread: false,
      message: `Дверь ответила без разбора по серии ${serial}`,
      bad: [],
    };
  }
  if (!row.ok) {
    return {
      ok: false,
      reread: false,
      message: refusalMessage(row.error ?? "", row.detail),
      bad: [],
    };
  }
  if (row.already) {
    // Перечитать всё равно НАДО: состояние уже было списанным, а карточка
    // показывала «Доступна» — значит вещь списал кто-то другой, пока экран был
    // открыт, и спорит с учётом именно он.
    return {
      ok: true,
      reread: true,
      message: `Серия ${row.serial} уже была списана`,
      note: "Второй записи в CRM не появилось — состояние уже было таким.",
      bad: [],
    };
  }
  // Номер списания назван вслух по той же причине, по какой в итоге по месту
  // названы номера серий: списание необратимо, и номер строки учёта —
  // единственное, чем человек проверит нас потом.
  const parts = [`Серия ${row.serial} списана`];
  if (row.spisanie_id) parts.push(`списание № ${row.spisanie_id}`);
  return { ok: true, reread: true, message: parts.join(" · "), bad: [] };
}

/**
 * Почему не списалось — словами склада, а не кодом двери.
 *
 * Коды тут от ОБЕИХ дверей: верхние — двери по баркоду и месту, нижние —
 * двери по номеру серии (`app/core/crm/spisanie.py`). Словарь общий, потому
 * что окно общее, а отказ «категория погашена» одинаково непонятен кодом с
 * любого экрана.
 *
 * Незнакомый код возвращается как есть: новый отказ двери обязан доехать до
 * экрана хоть чем-нибудь, а молчание читалось бы как «ничего не произошло».
 */
export function refusalMessage(code: string, detail?: string): string {
  const known: Record<string, string> = {
    nothing_here: "В этом месте таких вещей по учёту уже нет",
    not_enough: "Просили больше, чем числится в месте. Не списано ничего",
    barcode_unknown: "Такого баркода нет в CRM",
    barcode_not_numeric: "Баркод — только цифры",
    crm_write_disabled: "Запись в CRM выключена в этой полосе",
    crm_unavailable: "CRM недоступна",
    in_progress: "Прошлая попытка ещё выполняется",
    request_id_taken: "Этот ключ занят другим сотрудником",
    auth_unavailable: "База wms недоступна, повтор не защитить",
    too_many_attempts: "Слишком много попыток, подождите",
    unauthorized: "Сессия истекла — войдите заново",
    serial_not_found: "Такой серии нет в CRM",
    category_unknown: "Этой причины нет в справочнике CRM или она погашена",
    place_not_found: "Некуда переместить вещь: у города нет места «Списано»",
    place_changed: "Вещь уже лежит в другом месте. Не списано ничего",
    write_denied: "У учётки нет прав менять списание в этом контуре CRM",
    spisanie_failed: "CRM отказала в списании",
    internal: "Внутренняя ошибка — списание не состоялось, смотрите журнал",
  };
  return known[code] ?? detail ?? code;
}
